import React from "react";
import { useGetCurrentEiaFuelRateQuery } from "@api/graphql/generated/generated-types";
import { Tooltip } from "@src/common/components";
import {
  fuelTypeLabel,
  formatRatePeriod,
  formatRatePerGallon,
} from "@src/common/components/EiaFuelRateNote/EiaFuelRateNote";
import { useAuth } from "@src/common/hooks/useAuth";
import { useCourierLocale } from "@src/common/courierLocale";

interface CurrentEiaFuelRateNoticeProps {
  /**
   * The order's selected vehicle type, when one is chosen. Passed to the query; the
   * server resolves fuel type the same way pricing does (vehicle type's fuelType,
   * else GAS), so this component never re-implements that rule.
   */
  vehicleTypeId?: string;
}

/**
 * The EIA rate in effect right now, shown while an order is being quoted. Staff quote the
 * fuel rate as part of quoting a shipment, so it has to be readable before the order exists.
 *
 * Renders nothing when the courier has no EIA-based fuel surcharge configured — the query
 * returns null in that case, and an empty rate line would only be noise.
 */
export function CurrentEiaFuelRateNotice({
  vehicleTypeId,
}: CurrentEiaFuelRateNoticeProps) {
  const { courierId } = useAuth();
  const { currencyDisplay } = useCourierLocale();

  const { data } = useGetCurrentEiaFuelRateQuery({
    variables: { courierId: courierId || "", vehicleTypeId },
    skip: !courierId,
  });

  const currentEiaFuelRate = data?.currentEiaFuelRate;
  if (!currentEiaFuelRate) {
    return null;
  }

  const { ratePerGallon, percent, fuelType, ratePeriod } = currentEiaFuelRate;

  return (
    <Tooltip
      content={`EIA ${fuelTypeLabel(fuelType)} rate for the week of ${formatRatePeriod(
        ratePeriod,
      )}. Orders are priced from the rate in effect on their expected completion date.`}
    >
      <span className="text-xs text-slate-500 dark:text-gray-400 border border-slate-200 dark:border-gray-600 rounded-md px-2 py-0.5">
        Fuel {currencyDisplay}
        {formatRatePerGallon(ratePerGallon)}/gal &middot; {percent}%
      </span>
    </Tooltip>
  );
}
